import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle,
  DollarSign,
  Users,
  Clock,
  Target,
  Shield,
  TrendingUp,
  Layers,
  FileText,
  Handshake,
  Calendar,
  BarChart,
  Zap,
} from "lucide-react";
import {
  EngagementModelsSection,
  FAQSection,
  IndustriesSection,
  TechnologiesSection,
} from "@/components/CommonSections";
import { CASE_STUDIES } from "@/data/mock";

// Engagement Model Data
const ENGAGEMENT_MODELS_DATA = {
  "fixed-price": {
    title: "Fixed Price",
    subtitle: "Predictable Budgets, Defined Outcomes",
    description:
      "Our Fixed Price model is ideal for projects with well-defined requirements and clear deliverables. You get complete budget certainty with milestone-based payments, ensuring transparent progress tracking and on-time delivery.",
    image:
      "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&q=80",
    icon: DollarSign,
    color: "from-emerald-500 to-teal-600",
    bestFor: [
      "Projects with clearly defined scope and requirements",
      "Fixed budget initiatives with strict financial controls",
      "Short to medium-term projects (2-6 months)",
      "MVP development with specific feature sets",
      "Regulatory or compliance-driven projects",
    ],
    howItWorks: [
      {
        step: 1,
        title: "Requirements Gathering",
        desc: "Comprehensive analysis of your project scope, features, and deliverables",
      },
      {
        step: 2,
        title: "Detailed Estimation",
        desc: "Fixed quote based on effort, complexity, and timeline assessment",
      },
      {
        step: 3,
        title: "Milestone Planning",
        desc: "Clear milestones with defined deliverables and acceptance criteria",
      },
      {
        step: 4,
        title: "Development & Delivery",
        desc: "Agile execution with regular demos and milestone sign-offs",
      },
    ],
    benefits: [
      {
        icon: Shield,
        title: "Budget Certainty",
        desc: "Know exactly what you'll pay upfront—no surprises",
      },
      {
        icon: Target,
        title: "Clear Deliverables",
        desc: "Well-defined scope with documented requirements",
      },
      {
        icon: Calendar,
        title: "Predictable Timeline",
        desc: "Fixed deadlines with milestone-based tracking",
      },
      {
        icon: FileText,
        title: "Detailed Documentation",
        desc: "Comprehensive specs and acceptance criteria",
      },
    ],
    stats: [
      { value: "98%", label: "On-Budget Delivery" },
      { value: "120+", label: "Fixed Price Projects" },
      { value: "4.8/5", label: "Client Satisfaction" },
    ],
  },
  "fixed-team": {
    title: "Fixed Team",
    subtitle: "Your Dedicated Development Partner",
    description:
      "The Fixed Team model provides you with a dedicated team of experts who become an extension of your organization. This model offers deep collaboration, cultural alignment, and long-term commitment to your success.",
    image:
      "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80",
    icon: Users,
    color: "from-blue-500 to-indigo-600",
    bestFor: [
      "Long-term product development and maintenance",
      "Complex projects requiring deep domain expertise",
      "Organizations seeking to augment their in-house team",
      "Startups building their core product",
      "Enterprise digital transformation initiatives",
    ],
    howItWorks: [
      {
        step: 1,
        title: "Team Composition",
        desc: "We assemble a dedicated team based on your technology stack and needs",
      },
      {
        step: 2,
        title: "Onboarding & Integration",
        desc: "Team integrates with your workflows, tools, and communication channels",
      },
      {
        step: 3,
        title: "Continuous Collaboration",
        desc: "Daily standups, sprint planning, and seamless coordination",
      },
      {
        step: 4,
        title: "Scaling & Optimization",
        desc: "Flexible team scaling as your project evolves",
      },
    ],
    benefits: [
      {
        icon: Handshake,
        title: "Dedicated Resources",
        desc: "Team works exclusively on your project",
      },
      {
        icon: TrendingUp,
        title: "Knowledge Retention",
        desc: "Deep understanding of your business and codebase",
      },
      {
        icon: Layers,
        title: "Seamless Integration",
        desc: "Becomes a natural extension of your team",
      },
      {
        icon: Zap,
        title: "Rapid Scaling",
        desc: "Quick team expansion when needed",
      },
    ],
    stats: [
      { value: "15+", label: "Dedicated Teams" },
      { value: "3+ Years", label: "Avg. Partnership" },
      { value: "98%", label: "Client Retention" },
    ],
  },
  "time-and-material": {
    title: "Time & Material",
    subtitle: "Maximum Flexibility, Complete Transparency",
    description:
      "The Time & Material model offers unparalleled flexibility for projects with evolving requirements. You pay for actual time and resources used, with complete visibility into effort and costs, making it perfect for agile, iterative development.",
    image:
      "https://images.unsplash.com/photo-1553877522-43269d4ea984?w=800&q=80",
    icon: Clock,
    color: "from-purple-500 to-violet-600",
    bestFor: [
      "Projects with evolving or unclear requirements",
      "R&D and innovation initiatives",
      "Ongoing maintenance and enhancement work",
      "Agile projects with frequent pivots",
      "Discovery phases and proof of concepts",
    ],
    howItWorks: [
      {
        step: 1,
        title: "Resource Allocation",
        desc: "We assign skilled resources based on your project needs",
      },
      {
        step: 2,
        title: "Sprint Planning",
        desc: "Collaborative prioritization of features and tasks",
      },
      {
        step: 3,
        title: "Transparent Tracking",
        desc: "Real-time visibility into hours, progress, and costs",
      },
      {
        step: 4,
        title: "Flexible Adjustment",
        desc: "Scale up or down based on changing priorities",
      },
    ],
    benefits: [
      {
        icon: TrendingUp,
        title: "Complete Flexibility",
        desc: "Adapt scope and priorities as needed",
      },
      {
        icon: BarChart,
        title: "Full Transparency",
        desc: "Detailed timesheets and progress reports",
      },
      {
        icon: Zap,
        title: "Quick Start",
        desc: "Minimal upfront planning, faster kickoff",
      },
      {
        icon: Target,
        title: "Value Optimization",
        desc: "Pay only for what you use",
      },
    ],
    stats: [
      { value: "100+", label: "T&M Projects" },
      { value: "40%", label: "Avg. Cost Savings" },
      { value: "100%", label: "Billing Transparency" },
    ],
  },
};

const EngagementModelPage = () => {
  const location = useLocation();

  // Scroll to section based on hash
  useEffect(() => {
    if (location.hash) {
      const elementId = location.hash.replace("#", "");
      const element = document.getElementById(elementId);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    }
  }, [location]);
  const engagementSlugs = [
    "classroom-management-tutoring", // Fixed Price
    "ai-powered-healthcare-platform", // Fixed Team
    "edtech-learning-platform", // Time & Material
  ];
  const displayCaseStudies = CASE_STUDIES.filter((cs) =>
    engagementSlugs.includes(cs.slug),
  );

  // Get case studies
  // const displayCaseStudies = CASE_STUDIES.slice(0, 3);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
        </div>

        {/* Gradient Orbs */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#b32126]/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <Link
              to="/what-we-do"
              className="inline-flex items-center text-slate-400 hover:text-white mb-6 transition-colors text-sm"
            >
              <ArrowRight className="h-4 w-4 mr-2 rotate-180" />
              Back to What We Do
            </Link>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Engagement Models
              <span className="block text-2xl md:text-3xl font-normal text-slate-300 mt-4">
                Choose the Right Partnership for Your Success
              </span>
            </h1>

            <p className="text-lg md:text-xl text-slate-300 mb-10 leading-relaxed max-w-3xl mx-auto">
              Every project is different — scope, team size, timeline, risk
              tolerance. Our three engagement models are built to match exactly
              where you are, whether that's a defined spec with a fixed budget
              or an evolving product that needs maximum flexibility.
            </p>

            {/* Quick Navigation */}
            <div className="flex flex-wrap justify-center gap-4">
              {Object.entries(ENGAGEMENT_MODELS_DATA).map(([key, model]) => {
                const IconComponent = model.icon;
                return (
                  <a
                    key={key}
                    href={`#${key}`}
                    className="inline-flex items-center px-6 py-3 bg-white/10 backdrop-blur-sm text-white font-medium rounded-xl hover:bg-white/20 transition-all border border-white/20"
                  >
                    <IconComponent className="h-5 w-5 mr-2" />
                    {model.title}
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Overview */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Quick Comparison
            </h2>
            <p className="text-lg text-gray-600">
              Find the perfect fit for your project
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {Object.entries(ENGAGEMENT_MODELS_DATA).map(([key, model]) => {
              const IconComponent = model.icon;
              return (
                <a
                  key={key}
                  href={`#${key}`}
                  className="group bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all border border-gray-100 hover:border-[#b32126]/30"
                >
                  <div
                    className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${model.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}
                  >
                    <IconComponent className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-[#b32126] transition-colors">
                    {model.title}
                  </h3>
                  <p className="text-gray-600 mb-4">{model.subtitle}</p>
                  <div className="flex items-center text-[#b32126] font-medium">
                    Learn More{" "}
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Individual Model Sections */}
      {Object.entries(ENGAGEMENT_MODELS_DATA).map(([key, model], index) => {
        const IconComponent = model.icon;
        const isEven = index % 2 === 0;

        return (
          <section
            key={key}
            id={key}
            className={`py-20 scroll-mt-20 ${
              isEven ? "bg-white" : "bg-gray-50"
            }`}
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              {/* Header */}
              <div
                className={`grid lg:grid-cols-2 gap-12 items-center mb-16 ${
                  !isEven ? "lg:flex-row-reverse" : ""
                }`}
              >
                <div className={`${!isEven ? "lg:order-2" : ""}`}>
                  <div
                    className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold mb-4 bg-gradient-to-r ${model.color} text-white`}
                  >
                    <IconComponent className="h-4 w-4 mr-2" />
                    Engagement Model
                  </div>
                  <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                    {model.title}
                  </h2>
                  <p className="text-xl text-gray-600 mb-6">{model.subtitle}</p>
                  <p className="text-gray-600 leading-relaxed mb-8">
                    {model.description}
                  </p>

                  {/* Stats */}
                  <div className="flex flex-wrap gap-6">
                    {model.stats.map((stat, idx) => (
                      <div key={idx} className="text-center">
                        <div className="text-3xl font-bold text-[#b32126]">
                          {stat.value}
                        </div>
                        <div className="text-sm text-gray-500">
                          {stat.label}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className={`${!isEven ? "lg:order-1" : ""}`}>
                  <div className="relative">
                    <div
                      className={`absolute inset-0 bg-gradient-to-br ${model.color} rounded-3xl transform rotate-3 opacity-20`}
                    />
                    <img
                      src={model.image}
                      alt={model.title}
                      className="relative rounded-3xl shadow-2xl w-full h-80 object-cover"
                    />
                  </div>
                </div>
              </div>

              {/* Best For */}
              <div className="mb-16">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Best For
                </h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {model.bestFor.map((item, idx) => (
                    <div
                      key={idx}
                      className="flex items-start gap-3 p-4 bg-white rounded-xl shadow-sm border border-gray-100"
                    >
                      <CheckCircle className="h-6 w-6 text-[#b32126] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* How It Works */}
              <div className="mb-16">
                <h3 className="text-2xl font-bold text-gray-900 mb-8">
                  How It Works
                </h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {model.howItWorks.map((step, idx) => (
                    <div key={idx} className="relative">
                      {idx < model.howItWorks.length - 1 && (
                        <div
                          className="hidden lg:block absolute top-8 left-full w-full h-0.5 bg-gradient-to-r from-[#b32126] to-transparent z-0"
                          style={{ width: "100%" }}
                        />
                      )}
                      <div className="relative bg-white rounded-2xl p-6 shadow-sm border border-gray-100 h-full">
                        <div
                          className={`w-12 h-12 rounded-full bg-gradient-to-br ${model.color} text-white flex items-center justify-center font-bold text-lg mb-4`}
                        >
                          {step.step}
                        </div>
                        <h4 className="text-lg font-bold text-gray-900 mb-2">
                          {step.title}
                        </h4>
                        <p className="text-gray-600 text-sm">{step.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Benefits */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold text-gray-900 mb-8">
                  Key Benefits
                </h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {model.benefits.map((benefit, idx) => {
                    const BenefitIcon = benefit.icon;
                    return (
                      <div
                        key={idx}
                        className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-lg hover:border-[#b32126]/30 transition-all"
                      >
                        <div
                          className={`w-12 h-12 rounded-xl bg-gradient-to-br ${model.color} flex items-center justify-center mb-4`}
                        >
                          <BenefitIcon className="h-6 w-6 text-white" />
                        </div>
                        <h4 className="text-lg font-bold text-gray-900 mb-2">
                          {benefit.title}
                        </h4>
                        <p className="text-gray-600 text-sm">{benefit.desc}</p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* CTA */}
              <div className="text-center">
                <Link
                  to="/contact"
                  className={`inline-flex items-center px-8 py-4 bg-gradient-to-r ${model.color} text-white font-semibold rounded-xl hover:opacity-90 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5`}
                >
                  Get Started with {model.title}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </div>
            </div>
          </section>
        );
      })}

      {/* Industries Section */}
      <IndustriesSection
        title="Industries We Serve"
        subtitle="Startup Lab solutions across diverse industry verticals"
      />

      {/* Success Stories */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-flex items-center px-4 py-2 bg-green-50 text-green-600 rounded-full text-sm font-semibold mb-4">
              Proven Results
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Success Stories
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              See how we&apos;ve helped businesses achieve their goals
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {displayCaseStudies.map((study, index) => (
              <Link
                key={study.id}
                to={`/resources/case-studies/${study.slug}`}
                className={`group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all border border-gray-100 ${
                  index === 0 ? "ring-2 ring-[#b32126] ring-offset-2" : ""
                }`}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={study.image}
                    alt={study.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4 flex gap-2">
                    <span className="px-3 py-1 bg-[#b32126] text-white text-xs font-semibold rounded-full">
                      {study.industry}
                    </span>
                    {index === 0 && (
                      <span className="px-3 py-1 bg-green-600 text-white text-xs font-semibold rounded-full">
                        Featured
                      </span>
                    )}
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-sm text-gray-500 mb-1">
                    {study.clientType}
                  </p>
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-[#b32126] transition-colors">
                    {study.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {study.tagline}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {study.results.slice(0, 3).map((result, idx) => (
                      <span
                        key={idx}
                        className="text-xs bg-green-50 text-green-700 px-2.5 py-1 rounded-full font-medium"
                      >
                        {result.value} {result.label}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link
              to="/resources/case-studies"
              className="inline-flex items-center px-6 py-3 border-2 border-[#b32126] text-[#b32126] font-semibold rounded-lg hover:bg-[#b32126] hover:text-white transition-colors"
            >
              View All Case Studies
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Technologies */}
      <TechnologiesSection />

      {/* FAQ Section */}
      <FAQSection />

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-[#b32126] to-rose-600 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full">
            <defs>
              <pattern
                id="engagement-pattern"
                width="40"
                height="40"
                patternUnits="userSpaceOnUse"
              >
                <circle cx="20" cy="20" r="2" fill="white" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#engagement-pattern)" />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Not Sure Which Model is Right for You?
          </h2>
          <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
            Our team will help you choose the perfect engagement model based on
            your project requirements, timeline, and budget. Let's discuss your
            needs.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-10 py-5 bg-white text-[#b32126] font-bold rounded-xl hover:bg-gray-100 transition-colors shadow-lg text-lg"
          >
            Schedule a Consultation
            <ArrowRight className="ml-2 h-6 w-6" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default EngagementModelPage;
